import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  FiCalendar, FiFileText, FiPlus, FiPrinter
} from "react-icons/fi";
import { MdDelete } from "react-icons/md";
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Slide } from 'react-toastify';
import Select from 'react-select';
import jsPDF from 'jspdf';
import { toPng } from 'html-to-image';
import { BASE_URL, GET_EXPENSEMASTER, POST_EXPENSEBILLING } from '../utlilities/config';
import { getWebUserInfo, initializeWebUserInfo } from '../utlilities/webConfig';
import withAuth from '../common/auth';
import ExpenseVoucher, { VOUCHER_ITEMS_PER_PAGE } from '../components/ExpenseVoucher';

const isElectron = () => !!(window && window.process && window.process.type);

let ipcRenderer = null;
if (isElectron()) {
  ipcRenderer = window.require('electron').ipcRenderer;
}

const Spinner = () => (
  <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
    <div className="flex flex-col items-center gap-3">
      <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
      <p className="text-indigo-600 font-medium">Loading...</p>
    </div>
  </div>
);

const formatVoucherDisplayDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return String(dateString);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatMoney = (value) =>
  Number(value || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/** Allow empty or non-negative numeric strings; reject minus sign and negative values. */
const isNonNegativeAmountInput = (value) => {
  if (value === '' || value === null || value === undefined) return true;
  const str = String(value).trim();
  if (str.includes('-')) return false;
  const num = parseFloat(str);
  if (str !== '' && !Number.isFinite(num)) return false;
  return !(Number.isFinite(num) && num < 0);
};

/** Render text with ₹ via canvas so jsPDF shows a real rupee (Helvetica has no ₹ glyph). */
const drawRightAlignedCurrency = (doc, amountText, rightX, baselineY, options = {}) => {
  const {
    fontSize = 9,
    bold = false,
    paddingRight = 8,
  } = options;
  const label = `₹${amountText}`;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setFontSize(fontSize);
    doc.text(amountText, rightX - paddingRight, baselineY, { align: 'right' });
    return;
  }

  const dpr = 2;
  const fontCss = `${bold ? '700' : '400'} ${fontSize * dpr}px Arial, "Noto Sans", sans-serif`;
  ctx.font = fontCss;
  const measured = ctx.measureText(label);
  const textW = Math.ceil(measured.width);
  const textH = Math.ceil(fontSize * dpr * 1.45);
  canvas.width = Math.max(textW + 4, 8);
  canvas.height = textH;
  ctx.font = fontCss;
  ctx.fillStyle = '#000000';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, 2, textH / 2);

  const imgW = canvas.width / dpr;
  const imgH = canvas.height / dpr;
  const imgX = rightX - paddingRight - imgW;
  const imgY = baselineY - fontSize * 0.72;
  doc.addImage(canvas.toDataURL('image/png'), 'PNG', imgX, imgY, imgW, imgH);
};

const chunkVoucherItems = (items, size) => {
  const list = Array.isArray(items) ? items : [];
  if (list.length === 0) return [[]];
  const pages = [];
  for (let i = 0; i < list.length; i += size) {
    pages.push(list.slice(i, i + size));
  }
  return pages;
};

/** API returns data: [{ EntryID }] (or a plain object) — use EntryID as voucher number. */
const resolveExpenseVoucherNo = (response) => {
  const data = response?.data?.data;
  const entry = Array.isArray(data) ? data[0] : data;
  const voucherNo =
    entry?.EntryID ??
    entry?.entryId ??
    entry?.VoucherNo ??
    entry?.ReceiptNo ??
    response?.data?.EntryID ??
    '';
  return voucherNo === null || voucherNo === undefined || voucherNo === ''
    ? ''
    : String(voucherNo);
};

const generateExpenseVoucherPdf = ({
  generalSettings,
  userName,
  entryDate,
  paymentMode,
  expenseItems,
  grandTotal,
  voucherNo,
}) => {
  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'pt',
    format: 'a5',
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 28;
  const rightColX = pageWidth - margin;
  const contentWidth = rightColX - margin;
  const firmName = `${generalSettings?.FirstName || ''} ${generalSettings?.LastName || ''}`.trim() || 'Payment Voucher';
  const mobileText = generalSettings?.MobileNo2
    ? `${generalSettings.MobileNo1 || ''} , ${generalSettings.MobileNo2}`
    : (generalSettings?.MobileNo1 || '');
  const amountPad = 10;
  const pages = chunkVoucherItems(expenseItems, VOUCHER_ITEMS_PER_PAGE);
  const totalPages = pages.length;

  const colNoW = 36;
  const colCatW = 145;
  const colAmtW = 108;
  const colDescW = contentWidth - colNoW - colCatW - colAmtW;
  const colNoX = margin;
  const colCatX = colNoX + colNoW;
  const colDescX = colCatX + colCatW;
  const colAmtX = colDescX + colDescW;
  const amountRightX = rightColX;
  const footerReserve = 95;
  const headerH = 20;

  const drawVLine = (x, top, bottom) => {
    doc.line(x, top, x, bottom);
  };

  pages.forEach((pageItems, pageIndex) => {
    if (pageIndex > 0) {
      doc.addPage('a5', 'landscape');
    }

    const isLastPage = pageIndex === totalPages - 1;
    const startNo = pageIndex * VOUCHER_ITEMS_PER_PAGE;
    let y = 34;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(15);
    doc.text(firmName.toUpperCase(), margin, y);

    if (totalPages > 1) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.text(`Page ${pageIndex + 1} of ${totalPages}`, rightColX, y, { align: 'right' });
    }

    y += 15;
    if (mobileText) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9.5);
      doc.text(mobileText, margin, y);
    }

    y += 22;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    const title = 'PAYMENT VOUCHER';
    const titleWidth = doc.getTextWidth(title);
    const titleX = pageWidth / 2;
    doc.setLineWidth(0.9);
    doc.line(margin, y - 3, titleX - titleWidth / 2 - 10, y - 3);
    doc.line(titleX + titleWidth / 2 + 10, y - 3, rightColX, y - 3);
    doc.text(title, titleX, y, { align: 'center' });

    y += 16;
    const metaTop = y;
    const metaBottom = y + 42;
    doc.setLineWidth(1);
    doc.rect(margin, metaTop, contentWidth, metaBottom - metaTop);

    y = metaTop + 16;
    doc.setFontSize(9);
    const leftLabelX = margin + 10;
    const leftValueX = margin + 95;
    const rightLabelX = pageWidth / 2 + 30;
    const rightValueX = pageWidth / 2 + 110;

    doc.setFont('helvetica', 'normal');
    doc.text('Voucher No', leftLabelX, y);
    doc.setFont('helvetica', 'bold');
    doc.text(`:  ${voucherNo || '—'}`, leftValueX, y);

    doc.setFont('helvetica', 'normal');
    doc.text('Date', rightLabelX, y);
    doc.setFont('helvetica', 'bold');
    doc.text(`:  ${formatVoucherDisplayDate(entryDate)}`, rightValueX, y);

    y += 16;
    doc.setFont('helvetica', 'normal');
    doc.text('Payment Mode', leftLabelX, y);
    doc.setFont('helvetica', 'bold');
    doc.text(`:  ${paymentMode || 'Cash'}`, leftValueX, y);

    doc.setFont('helvetica', 'normal');
    doc.text('Prepared By', rightLabelX, y);
    doc.setFont('helvetica', 'bold');
    doc.text(`:  ${userName || '—'}`, rightValueX, y);

    y = metaBottom + 14;
    const tableTop = y;

    doc.setLineWidth(1);
    doc.rect(margin, tableTop, contentWidth, headerH);
    drawVLine(colCatX, tableTop, tableTop + headerH);
    drawVLine(colDescX, tableTop, tableTop + headerH);
    drawVLine(colAmtX, tableTop, tableTop + headerH);

    y = tableTop + 13;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.text('NO.', colNoX + colNoW / 2, y, { align: 'center' });
    doc.text('CATEGORY', colCatX + 6, y);
    doc.text('DESCRIPTION', colDescX + 6, y);
    doc.text('AMOUNT', amountRightX - amountPad, y, { align: 'right' });

    y = tableTop + headerH;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);

    let rowsBottom = y;

    pageItems.forEach((item, index) => {
      const category = String(item.category || item.name || '—');
      const description = String(item.description || '—');
      const amountText = formatMoney(item.amount);
      const descLines = doc.splitTextToSize(description, colDescW - 12);
      const catLines = doc.splitTextToSize(category, colCatW - 12);
      const rowH = Math.max(18, Math.max(descLines.slice(0, 2).length, catLines.slice(0, 2).length) * 11 + 6);

      doc.setLineWidth(0.6);
      doc.rect(margin, y, contentWidth, rowH);
      drawVLine(colCatX, y, y + rowH);
      drawVLine(colDescX, y, y + rowH);
      drawVLine(colAmtX, y, y + rowH);

      const textY = y + 12;
      doc.text(String(startNo + index + 1), colNoX + colNoW / 2, textY, { align: 'center' });
      doc.setFont('helvetica', 'bold');
      doc.text(catLines.slice(0, 2), colCatX + 6, textY);
      doc.setFont('helvetica', 'normal');
      doc.text(descLines.slice(0, 2), colDescX + 6, textY);
      drawRightAlignedCurrency(doc, amountText, amountRightX, textY, {
        fontSize: 8.5,
        bold: true,
        paddingRight: amountPad,
      });

      y += rowH;
      rowsBottom = y;
    });

    const minTableBottom = pageHeight - footerReserve;
    if (rowsBottom < minTableBottom) {
      doc.setLineWidth(0.6);
      doc.rect(margin, rowsBottom, contentWidth, minTableBottom - rowsBottom);
      drawVLine(colCatX, rowsBottom, minTableBottom);
      drawVLine(colDescX, rowsBottom, minTableBottom);
      drawVLine(colAmtX, rowsBottom, minTableBottom);
      rowsBottom = minTableBottom;
    }

    const totalH = 22;
    doc.setLineWidth(1.2);
    doc.rect(margin, rowsBottom, contentWidth, totalH);
    drawVLine(colAmtX, rowsBottom, rowsBottom + totalH);

    if (isLastPage) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.text('Total Amount', colAmtX - 8, rowsBottom + 15, { align: 'right' });
      drawRightAlignedCurrency(doc, formatMoney(grandTotal), amountRightX, rowsBottom + 15, {
        fontSize: 10,
        bold: true,
        paddingRight: amountPad,
      });

      const sigBoxWidth = 140;
      const sigX = rightColX - sigBoxWidth;
      const sigY = pageHeight - 36;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setLineWidth(0.8);
      doc.line(sigX, sigY, rightColX, sigY);
      doc.text('Authorized Signature', sigX + sigBoxWidth / 2, sigY + 12, { align: 'center' });
    } else {
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(9);
      doc.text('Continued on next page…', rightColX - 8, rowsBottom + 15, { align: 'right' });
    }
  });

  const safeDate = String(entryDate || '').replace(/[\\/:*?"<>|]/g, '-') || 'payment';
  const fileSuffix = voucherNo || Date.now();
  doc.save(`Payment_Voucher_${safeDate}_${fileSuffix}.pdf`);
};

const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '36px',
    height: '36px',
    backgroundColor: '#f9fafb',
    borderColor: state.isFocused ? '#6366f1' : '#b5b6b8ff',
    boxShadow: state.isFocused ? '0 0 0 1px #6366f1' : 'none',
    borderRadius: '0.5rem',
    '&:hover': {
      borderColor: '#6366f1',
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '2px 8px',
    height: '36px',
  }),
  input: (provided) => ({
    ...provided,
    margin: '0px',
    padding: '0px',
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    height: '36px',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? '#92c3fcff'
      : state.isFocused
      ? '#92c3fcff'
      : '#ffffff',
    color: state.isSelected ? '#000000ff' : state.isFocused ? '#000000' : '#000000',
    fontWeight: '500',
    padding: '6px 8px',
    fontSize: '0.85rem',
    '&:hover': {
      backgroundColor: '#92c3fcff',
      color: '#000000ff',
    },
  }),
  singleValue: (provided) => ({
    ...provided,
    fontSize: '0.9rem',
  }),
  placeholder: (provided) => ({
    ...provided,
    fontSize: '0.9rem',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 999,
    borderRadius: '0.5rem',
    overflow: 'hidden',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
};

const ExpenseEntry = () => {
  const token = localStorage.getItem('accessToken');
  const [loading, setLoading] = useState(false);
  const [entryDate, setEntryDate] = useState('');
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [expenseItems, setExpenseItems] = useState([{ category: null, amount: "", description: "", id: null, name: "" }]);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [currentDateTime, setCurrentDateTime] = useState('');
  const [paymentMode, setPaymentMode] = useState('Cash');
  const [voucherOutputMode, setVoucherOutputMode] = useState('pdf');
  const [generalSettings, setGeneralSettings] = useState({});
  const [userInfo, setUserInfo] = useState({});
  const [sysInfo, setSysInfo] = useState({});
  const [voucherPrintData, setVoucherPrintData] = useState(null);
  const todayDate = new Date().toISOString().split('T')[0];

  const expenseRefs = useRef([]);
  const submitbtnRef = useRef(null);
  const descriptionRefs = useRef([]);
  const amountRefs = useRef([]);

  const getExpenseCategories = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}${GET_EXPENSEMASTER}`, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`
        },
      });

      if (response.status === 200) {
        setExpenseCategories(response.data.data || []);
      }
    } catch (error) {
      toast.error('Failed to fetch expense categories.', {
        autoClose: 1000,
      });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    const fetchData = async () => {
      await getExpenseCategories();

      let settings = {};
      let user = {};
      let sysinfo = {};
      try {
        if (isElectron()) {
          settings = await ipcRenderer.invoke('store-get', 'generalopsettings');
          user = await ipcRenderer.invoke('store-get', 'userInfo');
          sysinfo = await ipcRenderer.invoke('store-get', 'sysInfo');
        } else {
          initializeWebUserInfo();
          settings = getWebUserInfo().generalopsettings;
          user = getWebUserInfo().userInfo;
          sysinfo = getWebUserInfo().sysInfo;
        }
      } catch (error) {
        console.error('Failed to load expense voucher settings:', error);
      }
      setGeneralSettings(settings || {});
      setUserInfo(user || {});
      setSysInfo(sysinfo || {});
    };

    fetchData();

    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    setEntryDate(`${year}-${month}-${day}`);

    setTimeout(() => {
      expenseRefs.current[0]?.focus();
    }, 500);
  }, [getExpenseCategories]);

  useEffect(() => {
    const updateDateTime = () => {
      const now = new Date();
      const formattedDateTime = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })}`;
      setCurrentDateTime(formattedDateTime);
    };

    updateDateTime();
    const interval = setInterval(updateDateTime, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleWheel = (e) => {
      if (e.target.type === 'number') {
        e.target.blur();
      }
    };

    const handleKeyDown = (e) => {
      if (e.target.type === 'number') {
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
          e.preventDefault();
        }
      }
    };

    document.addEventListener('wheel', handleWheel, { capture: true });
    document.addEventListener('keydown', handleKeyDown, { capture: true });

    return () => {
      document.removeEventListener('wheel', handleWheel, { capture: true });
      document.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, []);

  const handleExpenseCategoryChange = (index, selectedOption) => {
    if (!selectedOption) return;

    const newExpenseItems = expenseItems.map((item, i) => {
      if (i === index) {
        return {
          ...item,
          category: selectedOption,
          name: selectedOption.label,
          id: selectedOption.value
        };
      }
      return item;
    });

    setExpenseItems(newExpenseItems);

    setTimeout(() => {
      descriptionRefs.current[index]?.focus();
    }, 100);
  };

  const handleAmountChange = (index, newAmount) => {
    if (!isNonNegativeAmountInput(newAmount)) {
      toast.error('Amount cannot be negative.', { autoClose: 1000 });
      return;
    }
    const updatedExpenseItems = expenseItems.map((item, i) => {
      if (i === index) {
        return {
          ...item,
          amount: newAmount
        };
      }
      return item;
    });
    setExpenseItems(updatedExpenseItems);
  };

  const handleDescriptionChange = (index, newDescription) => {
    const updatedExpenseItems = expenseItems.map((item, i) => {
      if (i === index) {
        return {
          ...item,
          description: newDescription
        };
      }
      return item;
    });
    setExpenseItems(updatedExpenseItems);
  };

  const addExpenseRow = () => {
    setExpenseItems(prevItems => [...prevItems, { category: null, amount: "", description: "", id: null, name: "" }]);

    setTimeout(() => {
      expenseRefs.current[expenseItems.length]?.focus();
    }, 100);
  };

  const handleDeleteRow = (index) => {
    const newExpenseItems = expenseItems.filter((_, i) => i !== index);
    setExpenseItems(newExpenseItems.length > 0
      ? newExpenseItems
      : [{ category: null, amount: "", description: "", id: null, name: "" }]);
  };

  const expenseCategoryOptions = expenseCategories.map(category => ({
    value: category.CategoryId,
    label: category.CategoryName
  }));

  const calculateTotal = () => {
    return expenseItems.reduce((total, item) => total + parseFloat(item.amount || 0.00), 0.00).toFixed(2);
  };

  const resetForm = () => {
    setExpenseItems([{ category: null, amount: "", description: "", id: null, name: "" }]);
    setPaymentMode('Cash');
    setVoucherPrintData(null);
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    setEntryDate(`${year}-${month}-${day}`);
  };

  const resolvePrinterName = () =>
    sysInfo?.OpBillPrinter || sysInfo?.pharmacyBillPriinter || '';

  const printExpenseVoucher = useCallback(async () => {
    if (!isElectron() || !ipcRenderer) {
      toast.error('Printing is only available in the desktop app', { autoClose: 1500 });
      return false;
    }

    const printerName = resolvePrinterName();
    if (!printerName) {
      toast.error('Cannot Print, Printer Not Selected', { autoClose: 1500 });
      return false;
    }

    try {
      await new Promise((resolve) => {
        requestAnimationFrame(() => {
          setTimeout(resolve, 500);
        });
      });

      const voucherPages = Array.from(
        document.querySelectorAll('[id^="expenseVoucherPage-"]')
      ).sort((a, b) => {
        const aIdx = Number(a.dataset.voucherPage ?? a.id.replace('expenseVoucherPage-', ''));
        const bIdx = Number(b.dataset.voucherPage ?? b.id.replace('expenseVoucherPage-', ''));
        return aIdx - bIdx;
      });

      if (voucherPages.length === 0) {
        toast.error('Voucher element not found', { autoClose: 1500 });
        return false;
      }

      for (let i = 0; i < voucherPages.length; i += 1) {
        const imgDataUrl = await toPng(voucherPages[i], {
          quality: 1,
          pixelRatio: 4,
          backgroundColor: '#ffffff',
        });

        await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            resolve();
          }, 8000);

          ipcRenderer.once('print-success', (_event, { status, error }) => {
            clearTimeout(timeout);
            if (status === 'success') {
              resolve();
            } else {
              reject(new Error(error || 'Print failed'));
            }
          });

          ipcRenderer.send('print-bill-image', {
            imgDataUrl,
            printer: printerName,
          });
        });

        if (i < voucherPages.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 400));
        }
      }

      toast.success(
        voucherPages.length > 1
          ? `Expense voucher sent to printer (${voucherPages.length} pages)`
          : 'Expense voucher sent to printer',
        { autoClose: 1000 }
      );
      return true;
    } catch (error) {
      console.error('Failed to print expense voucher:', error);
      toast.error('Failed to print expense voucher. Check printer settings.', {
        autoClose: 1500,
      });
      return false;
    }
  }, [sysInfo?.OpBillPrinter, sysInfo?.pharmacyBillPriinter]);

  const handleSave = async () => {
    const validExpenseItems = expenseItems.filter(item => item.category && item.amount);

    if (validExpenseItems.length === 0) {
      toast.error('Please add at least one expense item.', { autoClose: 1000 });
      return;
    }

    if (!entryDate) {
      toast.error('Please select entry date.', { autoClose: 1000 });
      return;
    }

    const hasNegativeAmount = validExpenseItems.some(
      (item) => parseFloat(item.amount) < 0
    );
    if (hasNegativeAmount) {
      toast.error('Amount cannot be negative.', { autoClose: 1000 });
      return;
    }

    setIsButtonDisabled(true);
    setLoading(true);

    const totalAmount = parseFloat(calculateTotal());
    const payload = {
      entryDate: entryDate,
      grandTotal: totalAmount,
      department: "Pharmacy",
      cashAmount: paymentMode === 'Cash' ? totalAmount : 0,
      bankAmount: paymentMode === 'Bank' ? totalAmount : 0,
      expenseItems: validExpenseItems.map(item => ({
        category: item.category.label,
        amount: parseFloat(item.amount),
        description: item.description
      }))
    };

    const voucherItems = validExpenseItems.map((item) => ({
      category: item.category?.label || item.name,
      description: item.description,
      amount: parseFloat(item.amount) || 0,
    }));

    try {
      const response = await axios.post(`${BASE_URL}${POST_EXPENSEBILLING}`, payload, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      });

      if (response.status === 200) {
        toast.success('Expense entry saved successfully', { autoClose: 1000 });

        const voucherNo = resolveExpenseVoucherNo(response);

        const voucherPayload = {
          entryDate,
          paymentMode,
          expenseItems: voucherItems,
          grandTotal: totalAmount,
          voucherNo,
          username: userInfo?.UserName || userInfo?.userName || '',
        };

        try {
          if (voucherOutputMode === 'print') {
            setVoucherPrintData(voucherPayload);
            await new Promise((resolve) => setTimeout(resolve, 600));
            await printExpenseVoucher();
          } else {
            generateExpenseVoucherPdf({
              generalSettings,
              userName: voucherPayload.username,
              entryDate: voucherPayload.entryDate,
              paymentMode: voucherPayload.paymentMode,
              expenseItems: voucherPayload.expenseItems,
              grandTotal: voucherPayload.grandTotal,
              voucherNo: voucherPayload.voucherNo,
            });
            toast.success('Expense voucher downloaded', { autoClose: 1000 });
          }
        } catch (voucherError) {
          console.error('Expense voucher output error:', voucherError);
          toast.error(
            voucherOutputMode === 'print'
              ? 'Expense saved, but printing failed'
              : 'Expense saved, but voucher PDF failed',
            { autoClose: 1500 }
          );
        }

        setTimeout(() => {
          resetForm();
          setLoading(false);
          setIsButtonDisabled(false);
        }, 1000);
      } else {
        toast.error('Failed to save expense entry', { autoClose: 1000 });
        setLoading(false);
        setIsButtonDisabled(false);
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to save expense entry', { autoClose: 1000 });
      setLoading(false);
      setIsButtonDisabled(false);
    }
  };

  return (
    <div className="flex h-full bg-background">
      {/* LeftMenu provided by TabbedLayout */}
      <ToastContainer
        position="top-center"
        autoClose={1000}
        limit={1}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Slide}
      />

      {loading && <Spinner />}

      <div className="flex-1 flex h-full flex-col overflow-auto hide-scrollbar">
        <div className="bg-white p-3 mb-3">
          <div className="flex justify-between items-center gap-4">
            <div>
              <h1 className="text-lg font-bold text-gray-800 flex items-center">
                Expense Entry
              </h1>
              <p className="text-xs text-gray-500 mt-0.5">{currentDateTime || new Date().toLocaleString()}</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="inline-flex rounded-md border border-gray-300 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setVoucherOutputMode('print')}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold transition-colors ${
                    voucherOutputMode === 'print'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                  title="Print voucher after save"
                >
                  <FiPrinter size={14} />
                  Print
                </button>
                <button
                  type="button"
                  onClick={() => setVoucherOutputMode('pdf')}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border-l border-gray-300 transition-colors ${
                    voucherOutputMode === 'pdf'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                  title="Download voucher PDF after save"
                >
                  <FiFileText size={14} />
                  PDF
                </button>
              </div>
              <button
                type="button"
                className="px-6 py-1.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold text-sm rounded-md hover:from-indigo-700 hover:to-purple-700 shadow-md hover:shadow-lg transition-all"
                onClick={handleSave}
                disabled={isButtonDisabled}
                ref={submitbtnRef}
              >
                Save
              </button>
              <button
                type="button"
                className="px-6 py-1.5 bg-gray-600 text-white font-semibold text-sm rounded-md hover:bg-gray-700 shadow-md hover:shadow-lg transition-all"
                onClick={resetForm}
              >
                New +
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white ml-5 mr-5 rounded-lg shadow-sm p-3 mb-3">
          <div className="w-full flex flex-row gap-3">
            <div className="w-full flex p-3 bg-gray-50 rounded-lg border border-gray-200 flex-col gap-2.5">
              <div className="flex items-center gap-2 pb-2 border-b border-black border-1">
                <FiCalendar className="text-indigo-600" size={16} />
                <h3 className="font-semibold text-gray-800 text-sm">Entry Information</h3>
              </div>

              <div className="flex items-center gap-4 mb-2">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Entry Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    className="px-3 py-2 border border-gray-300 text-black font-medium bg-white rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all text-sm"
                    value={entryDate}
                    max={todayDate}
                    onChange={(e) => {
                      const selectedDate = e.target.value;
                      setEntryDate(selectedDate > todayDate ? todayDate : selectedDate);
                    }}
                  />
                </div>
              </div>

              <div className="flex items-center gap-2 pb-2 border-b border-black border-1 mb-1">
                <h3 className="font-semibold text-gray-800 text-sm">Expense Items</h3>
                <div className="ml-auto">
                  <button
                    type="button"
                    className="p-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors shadow-sm"
                    onClick={addExpenseRow}
                    title="Add new expense item"
                  >
                    <FiPlus size={16} />
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
                <table className="w-full border-collapse">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="p-3 text-left text-xs font-medium text-black uppercase tracking-wider">No.</th>
                      <th className="p-3 text-left text-xs font-medium text-black uppercase tracking-wider">Category</th>
                      <th className="p-3 text-left text-xs font-medium text-black uppercase tracking-wider">Description</th>
                      <th className="p-3 text-left text-xs font-medium text-black uppercase tracking-wider">Amount</th>
                      <th className="p-3 text-left text-xs font-medium text-black uppercase tracking-wider">Action</th>
                    </tr>
                  </thead>

                  <tbody className="bg-white divide-y divide-gray-200">
                    {expenseItems.length > 0 ? (
                      expenseItems.map((item, index) => (
                        <tr
                          key={index}
                          className={`${
                            selectedIndex === index
                              ? "bg-indigo-50"
                              : "hover:bg-gray-50"
                          } transition-colors`}
                          onClick={() => setSelectedIndex(index)}
                        >
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                            {index + 1}
                          </td>

                          <td className="px-3 py-2" style={{ minWidth: '200px' }}>
                            <Select
                              options={expenseCategoryOptions}
                              className="w-full"
                              isSearchable
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              value={item.category}
                              onChange={(selectedOption) => handleExpenseCategoryChange(index, selectedOption)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  const selectInstance = expenseRefs.current[index];
                                  const isMenuOpen = selectInstance?.state?.menuIsOpen;
                                  const focusedOption = selectInstance?.state?.focusedOption;

                                  if (!isMenuOpen && !focusedOption) {
                                    e.preventDefault();
                                    descriptionRefs.current[index]?.focus();
                                  }
                                }
                              }}
                              required
                              ref={(el) => (expenseRefs.current[index] = el)}
                              styles={customSelectStyles}
                              placeholder="Select Category"
                            />
                          </td>

                          <td className="px-3 py-2" style={{ minWidth: '250px' }}>
                            <input
                              type="text"
                              className="w-full px-3 py-2 border border-gray-300 text-black font-medium bg-white rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all text-sm placeholder:text-gray-500"
                              value={item.description || ''}
                              onChange={(e) => handleDescriptionChange(index, e.target.value)}
                              placeholder="Enter description"
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  amountRefs.current[index]?.focus();
                                }
                              }}
                              ref={(el) => (descriptionRefs.current[index] = el)}
                              onFocus={(e) => e.target.select()}
                            />
                          </td>

                          <td className="px-3 py-2 whitespace-nowrap text-sm" style={{ minWidth: '120px' }}>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              className="w-full px-3 py-2 border border-gray-300 text-black font-medium bg-white rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors text-right text-sm placeholder:text-gray-500"
                              value={item.amount || ''}
                              onChange={(e) => handleAmountChange(index, e.target.value)}
                              placeholder="0.00"
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  if (index === expenseItems.length - 1) {
                                    const amount = parseFloat(item.amount || 0);

                                    if (!item.amount || amount === 0) {
                                      submitbtnRef.current?.focus();
                                    } else {
                                      addExpenseRow();
                                      setTimeout(() => {
                                        expenseRefs.current[expenseItems.length]?.focus();
                                      }, 100);
                                    }
                                  } else if (expenseRefs.current[index + 1]) {
                                    expenseRefs.current[index + 1]?.focus();
                                  } else {
                                    submitbtnRef.current?.focus();
                                  }
                                }
                              }}
                              ref={(el) => (amountRefs.current[index] = el)}
                              onFocus={(e) => e.target.select()}
                            />
                          </td>

                          <td className="px-3 py-2 whitespace-nowrap text-sm">
                            <button
                              type="button"
                              className="p-2 bg-red-50 text-red-500 rounded-md hover:bg-red-100 transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteRow(index);
                              }}
                              title="Remove expense"
                            >
                              <MdDelete size={16} />
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="px-3 py-6 text-center text-sm text-gray-500">
                          No expenses added. Click the plus button to add an expense.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="w-[37%] flex flex-col gap-3 h-full sticky top-0">
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 h-full overflow-auto flex flex-col">
                <div className="flex justify-between items-center mb-3 pb-2 border-b border-black border-1">
                  <h2 className="font-semibold text-gray-800 text-sm">Expense Summary</h2>
                  <div className="text-xs text-gray-600">
                    {expenseItems.filter(item => item.category && item.amount).length} items
                  </div>
                </div>

                <div className="bg-white p-3 rounded-lg mb-4 border border-gray-200">
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-medium text-gray-600">Entry Date</span>
                    <span className="text-xs font-bold text-indigo-600">
                      {entryDate ? new Date(entryDate).toLocaleDateString() : 'Not set'}
                    </span>
                  </div>
                </div>

                <div className="space-y-2 flex-grow overflow-y-auto">
                  {expenseItems.filter(item => item.category && item.amount).map((item, index) => (
                    <div key={index} className="bg-white p-3 rounded-lg border border-gray-200">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="font-medium text-xs text-gray-800">{item.category?.label}</div>
                          <div className="text-xs text-gray-600 mt-1 line-clamp-2">{item.description}</div>
                        </div>
                        <div className="text-xs font-bold text-indigo-600 ml-2">
                          ₹{parseFloat(item.amount || 0).toFixed(2)}
                        </div>
                      </div>
                    </div>
                  ))}

                  {expenseItems.filter(item => item.category && item.amount).length === 0 && (
                    <div className="text-center text-gray-500 text-xs py-8">
                      No expenses added yet
                    </div>
                  )}
                </div>

                <div className="flex justify-between items-center py-2 border-t border-b border-gray-200 my-4">
                  <span className="font-bold text-sm text-gray-800">Total Amount</span>
                  <span className="font-bold text-base text-indigo-600">
                    ₹{calculateTotal()}
                  </span>
                </div>

                <div className="mt-2">
                  <label className="block text-xs font-semibold text-gray-700 mb-2">
                    Payment Mode
                  </label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setPaymentMode('Cash')}
                      className={`flex-1 px-4 py-2.5 rounded-lg font-medium text-sm transition-all ${
                        paymentMode === 'Cash'
                          ? 'bg-indigo-600 text-white shadow-md'
                          : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                      }`}
                    >
                      Cash
                    </button>
                    <button
                      type="button"
                      onClick={() => setPaymentMode('Bank')}
                      className={`flex-1 px-4 py-2.5 rounded-lg font-medium text-sm transition-all ${
                        paymentMode === 'Bank'
                          ? 'bg-indigo-600 text-white shadow-md'
                          : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                      }`}
                    >
                      Bank
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ position: 'absolute', opacity: 0, top: '-10000px', left: '-10000px', pointerEvents: 'none' }}>
        <ExpenseVoucher
          generalSetting={generalSettings}
          username={
            voucherPrintData?.username ||
            userInfo?.UserName ||
            userInfo?.userName ||
            ''
          }
          entryDate={voucherPrintData?.entryDate || entryDate}
          paymentMode={voucherPrintData?.paymentMode || paymentMode}
          expenseItems={
            voucherPrintData?.expenseItems ||
            expenseItems
              .filter((item) => item.category && item.amount)
              .map((item) => ({
                category: item.category?.label || item.name,
                description: item.description,
                amount: parseFloat(item.amount) || 0,
              }))
          }
          grandTotal={
            voucherPrintData?.grandTotal ??
            parseFloat(calculateTotal())
          }
          voucherNo={voucherPrintData?.voucherNo || ''}
        />
      </div>
    </div>
  );
};

export default withAuth(ExpenseEntry);
